<template>
  <section class="config-expression">
    <div class="exp-content">
      <div class="selection">
        <div class="content">
          <div class="field">
            <section class="config-field inner-element">
              <div class="op-name" v-if="selectedOp">
                <div>{{ opTitle }}</div>
              </div>

              <search-box-simple
                v-if="!selectedOp"
                ref="operandSelect"
                :list="operandList"
                @on-search-box-item-selected="onOperandSelected"
              />

              <div class="math-ops" v-if="selectedOp && !isConcat">
                <search-box-simple
                  v-if="!isDateTrunc"
                  :list="calculatedList"
                  :value="firstOpField"
                  @on-search-box-item-selected="onFirstOpFieldSelected"
                />

                <search-box-simple
                  v-if="isMathOp"
                  :value="secondOpField"
                  :list="calculatedList"
                  @on-search-box-item-selected="onSecondOpFieldSelected"
                />
              </div>

              <div class="list-exp-item" v-if="selectedOp && isConcat">
                <draggable
                  v-model="concatArgs"
                  @start="drag = true"
                  @end="drag = false"
                  @change="onArgChange"
                >
                  <search-box-simple
                    v-for="a in concatArgs"
                    :id="a.argId"
                    :key="a.argId"
                    :list="calculatedList"
                    :value="a.name || null"
                    :is-from-concat="true"
                    :has-drag-icon="true"
                    static-data-type="string"
                    @on-search-box-item-selected="onConcatItemSelected"
                  />
                </draggable>
              </div>

              <div class="date-op" v-if="selectedOp && isDateTrunc">
                <div>{{ getMessageText(messages.EXP_LABEL_DATE) }}</div>
                <search-box-simple
                  :list="calculatedList"
                  :value="firstOpField"
                  @on-search-box-item-selected="onFirstOpFieldSelected"
                />
                <div>{{ $t("period") }}</div>
                <search-box-simple
                  :list="dateIntervalList"
                  :value="intervalVal"
                  @on-search-box-item-selected="onDateIntervalSelected"
                />
              </div>
            </section>
          </div>
        </div>
      </div>
      <div tabindex="0" class="close-icon-wrapper">
        <svg class="close-icon" @keydown.enter.stop="resetOp" @click="resetOp">
          <use
            xlink:href="@/assets/icons/utility-sprite/svg/symbols.svg#close"
          ></use>
        </svg>
      </div>
    </div>
  </section>
</template>

<script>
  import draggable from "vuedraggable";
  import SearchBoxSimple from "@/components/SearchBoxSimple";
  import { mapState } from "vuex";
  import {
    CRM_DATA_ENGINE_DIMENSIONS,
    DATE_INTERVALS,
    CRM_DATA_ENGINE_OPERAND_TYPE,
    CRM_DATA_ENGINE_METRICS,
    STUDIO_DATA_TYPE
  } from "@/static/global-variables";
  import { v4 as uuidv4 } from "uuid";
  export default {
    name: "FieldWithOperand",
    components: {
      SearchBoxSimple,
      draggable
    },
    props: {
      info: {
        type: Object
      },
      fields: {
        type: Array
      }
    },
    data() {
      return {
        asText: "",
        selectedOp: null,
        calculatedList: [],
        concatArgs: []
      };
    },
    computed: {
      ...mapState({
        messages: "messages"
      }),
      opTitle() {
        if (this.selectedOp) {
          switch (this.selectedOp.type) {
            case CRM_DATA_ENGINE_OPERAND_TYPE.CONCAT:
              return this.getMessageText(this.messages.EXP_CONCAT);
            case CRM_DATA_ENGINE_OPERAND_TYPE.DATE_TRUNC:
              return this.$t("endOfCalendarPeriod");
            case CRM_DATA_ENGINE_OPERAND_TYPE.DIV:
              return this.getMessageText(this.messages.EXP_DIVIDE_NUM);
            case CRM_DATA_ENGINE_OPERAND_TYPE.MULTI:
              return this.getMessageText(this.messages.EXP_MULTIPLY_NUM);
            case CRM_DATA_ENGINE_OPERAND_TYPE.MINUS:
              return this.getMessageText(this.messages.EXP_SUBTRACT_NUM);
            case CRM_DATA_ENGINE_OPERAND_TYPE.PLUS:
              return this.getMessageText(this.messages.EXP_ADD_NUM);
            case CRM_DATA_ENGINE_OPERAND_TYPE.SUM:
              return this.getMessageText(this.messages.EXP_SUM);
            case CRM_DATA_ENGINE_OPERAND_TYPE.COUNT:
              return this.$t("count");
            case CRM_DATA_ENGINE_OPERAND_TYPE.COUNT_DISTINCT:
              return this.getMessageText(this.messages.EXP_COUNT_UNIQUE);
            case CRM_DATA_ENGINE_OPERAND_TYPE.MAX:
              return this.getMessageText(this.messages.EXP_MAX);
            case CRM_DATA_ENGINE_OPERAND_TYPE.MIN:
              return this.getMessageText(this.messages.EXP_MIN);
          }
        }

        return "";
      },
      dateIntervalList() {
        return Object.keys(DATE_INTERVALS).map(i => {
          return { name: this.$t(i), key: i };
        });
      },
      isConcat() {
        return (
          this.selectedOp &&
          this.selectedOp.type === CRM_DATA_ENGINE_OPERAND_TYPE.CONCAT
        );
      },
      isDateTrunc() {
        return (
          this.selectedOp &&
          this.selectedOp.type === CRM_DATA_ENGINE_OPERAND_TYPE.DATE_TRUNC
        );
      },
      firstOpField() {
        return this.info.fieldA || this.info.field || null;
      },
      secondOpField() {
        return this.info.fieldB || null;
      },
      intervalVal() {
        return this.info.interval || null;
      },
      isMathOp() {
        const mathOps = [
          CRM_DATA_ENGINE_OPERAND_TYPE.DIV,
          CRM_DATA_ENGINE_OPERAND_TYPE.MULTI,
          CRM_DATA_ENGINE_OPERAND_TYPE.MINUS,
          CRM_DATA_ENGINE_OPERAND_TYPE.PLUS
        ];

        return this.selectedOp && mathOps.includes(this.selectedOp.type);
      },
      operandList() {
        let list = CRM_DATA_ENGINE_DIMENSIONS.concat(CRM_DATA_ENGINE_METRICS);
        return list
          .map(l => {
            l.label = this.getOpLabel(l.type);
            return l;
          })
          .filter(o => {
            return o.type !== CRM_DATA_ENGINE_OPERAND_TYPE.QUOTE;
          });
      }
    },
    mounted() {
      if (this.info) {
        if (this.info.arguments) {
          this.concatArgs = this.info.arguments.map(a => {
            let n = {};
            if (!a.argId) {
              n.argId = uuidv4();
              n.name = a;
            }
            return n;
          });
        }
        if (this.info.type) {
          this.onOperandSelected({
            type: this.info.type,
            label: this.getOpLabel(this.info.type)
          });
        }
      }
    },
    methods: {
      getOpLabel(type) {
        if (!type) {
          return "";
        }

        switch (type) {
          case CRM_DATA_ENGINE_OPERAND_TYPE.CONCAT:
            return this.getMessageText(this.messages.EXP_CONCAT);
          case CRM_DATA_ENGINE_OPERAND_TYPE.DATE_TRUNC:
            return this.$t("endOfCalendarPeriod");
          case CRM_DATA_ENGINE_OPERAND_TYPE.DIV:
            return this.getMessageText(this.messages.EXP_DIVIDE_NUM);
          case CRM_DATA_ENGINE_OPERAND_TYPE.MULTI:
            return this.getMessageText(this.messages.EXP_MULTIPLY_NUM);
          case CRM_DATA_ENGINE_OPERAND_TYPE.MINUS:
            return this.getMessageText(this.messages.EXP_SUBTRACT_NUM);
          case CRM_DATA_ENGINE_OPERAND_TYPE.PLUS:
            return this.getMessageText(this.messages.EXP_ADD_NUM);
          case CRM_DATA_ENGINE_OPERAND_TYPE.SUM:
            return this.getMessageText(this.messages.EXP_SUM);
          case CRM_DATA_ENGINE_OPERAND_TYPE.COUNT:
            return this.$t("count");
          case CRM_DATA_ENGINE_OPERAND_TYPE.COUNT_DISTINCT:
            return this.getMessageText(this.messages.EXP_COUNT_UNIQUE);
          case CRM_DATA_ENGINE_OPERAND_TYPE.MAX:
            return this.getMessageText(this.messages.EXP_MAX);
          case CRM_DATA_ENGINE_OPERAND_TYPE.MIN:
            return this.getMessageText(this.messages.EXP_MIN);
        }
      },
      resetOp() {
        this.selectedOp = null;
        this.concatArgs = [];
        this.onFirstOpFieldSelected(null);
        if (this.isMathOp) {
          this.onSecondOpFieldSelected(null);
        }
        this.$emit("on-op-update", null, this.info.id);
      },

      onOperandSelected(ctx) {
        if (!ctx || ctx.isEmpty) {
          this.selectedOp = null;
        } else {
          this.selectedOp = ctx;
        }
        if (this.isMathOp) {
          this.calculatedList = this.fields.filter(f => {
            return f.type === STUDIO_DATA_TYPE.NUMBER;
          });
        } else if (this.isDateTrunc) {
          this.calculatedList = this.fields.filter(f => {
            return (
              f.type === STUDIO_DATA_TYPE.DATE ||
              f.type === STUDIO_DATA_TYPE.DATE_TIME
            );
          });
        } else if (
          this.selectedOp &&
          this.selectedOp.type === CRM_DATA_ENGINE_OPERAND_TYPE.SUM
        ) {
          this.calculatedList = this.fields.filter(f => {
            return f.type === STUDIO_DATA_TYPE.NUMBER;
          });
        } else if (
          this.selectedOp &&
          (this.selectedOp.type === CRM_DATA_ENGINE_OPERAND_TYPE.COUNT ||
            this.selectedOp.type ===
              CRM_DATA_ENGINE_OPERAND_TYPE.COUNT_DISTINCT)
        ) {
          this.calculatedList = this.fields;
        } else if (
          this.selectedOp &&
          (this.selectedOp.type === CRM_DATA_ENGINE_OPERAND_TYPE.MAX ||
            this.selectedOp.type === CRM_DATA_ENGINE_OPERAND_TYPE.MIN)
        ) {
          this.calculatedList = this.fields.filter(f => {
            return f.type === STUDIO_DATA_TYPE.NUMBER;
          });
        } else if (
          this.selectedOp &&
          this.selectedOp.type === CRM_DATA_ENGINE_OPERAND_TYPE.CONCAT
        ) {
          this.addConcatArg();
          this.calculatedList = this.fields;
        }
      },
      addConcatArg() {
        this.concatArgs.push({ argId: uuidv4(), name: null });
      },
      onDateIntervalSelected(ctx) {
        this.$emit(
          "on-op-update",
          this.selectedOp.type,
          this.info.id,
          "interval",
          ctx
        );
      },
      onArgChange() {
        let formatted = this.concatArgs.concat([]).filter(c => {
          return c.name !== null;
        });
        this.$emit("on-arg-change", this.info.id, formatted);
      },
      onConcatItemSelected(ctx) {
        let i = this.concatArgs.findIndex(a => {
          return a.argId === ctx.searchBoxId;
        });
        if (ctx.isEmpty) {
          this.concatArgs[i] = { argId: this.concatArgs[i].argId, name: null };
        } else {
          this.concatArgs[i].name = ctx;
        }

        let l = this.concatArgs.filter(a => {
          return a.name === null;
        });
        if (l.length < 1 && ctx) {
          this.addConcatArg();
        }
        this.$emit(
          "on-op-update",
          this.selectedOp.type,
          this.info.id,
          "arguments",
          ctx,
          i
        );
      },
      onFirstOpFieldSelected(ctx) {
        let propName = this.isMathOp ? "fieldA" : "field";
        this.$emit(
          "on-op-update",
          this.selectedOp ? this.selectedOp.type : "",
          this.info.id,
          propName,
          ctx
        );
      },
      onSecondOpFieldSelected(ctx) {
        this.$emit(
          "on-op-update",
          this.selectedOp.type,
          this.info.id,
          "fieldB",
          ctx
        );
      }
    }
  };
</script>

<style lang="scss">
  $border-style: 1px solid var(--LIGHT_GRAY);
  $exp-font-size: 0.75rem;
  @mixin exp-icon {
    height: 1rem;
    width: 1rem;
    fill: var(--DARK_GREY);
    cursor: pointer;
    margin-right: 0.3rem;
  }
  .config-expression {
    border: $border-style;
    width: 100%;
    box-sizing: border-box;
    position: relative;
    padding: 0.3rem 0.9rem 0.3rem 0.5rem;
    font-size: $exp-font-size;
    .exp-content {
      display: flex;
      justify-content: space-between;
      .selection {
        width: 100%;
        .content .config-field {
          border: $border-style;
          width: 100%;
          box-sizing: border-box;
          position: relative;
          padding: 1.25rem 0.4rem 0.8rem 0.4rem;
          font-size: $exp-font-size;
          border: none;
        }
      }
    }
  }

  .math-ops > section,
  .date-op > section {
    padding-bottom: 1rem;
  }

  .exp-content {
    position: relative;

    .config-expression .exp-content .selection .content {
      margin-top: 0.5rem;
    }

    .close-icon-wrapper {
      height: 1rem;
      width: 1rem;
      position: absolute;
      top: 0;
      right: 0;

      .close-icon {
        @include exp-icon;
        position: absolute;
        top: 0;
        right: 0;
        margin: 0;
      }
    }
  }
</style>
